import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { botsApi, notificationsApi, tasksApi, CLIENT_ID } from '../services/api';
import { queryClient } from '../lib/queryClient';
import { openLiveStream } from '../lib/liveStream';
import i18n from '../i18n';
import BotAvatar from '../components/bots/BotAvatar';
import { botOf, notificationUrl } from '../components/ui/NotificationItem';
import { chatIdFromUrl } from '../components/bots/chatLinks';

const NotificationsContext = createContext(null);

// A bot's news about the chat on screen needs no toast: the chat shows it (old and new links alike).
const isAboutOpenChat = (n) => {
  if (n.type !== 'bot_update') return false;
  const chat = chatIdFromUrl(n.action_url || '');
  return !!chat && chat === chatIdFromUrl(window.location.href);
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

// Fallback toast titles, read in the app's language when a toast is shown.
const TYPE_LABEL_KEYS = {
  task_assigned: 'ui_common.notification_types.task_assigned',
  task_commented: 'ui_common.notification_types.task_commented',
  task_mentioned: 'ui_common.notification_types.task_mentioned',
  team_invite_accepted: 'ui_common.notification_types.team_invite_accepted',
  task_reminder: 'ui_common.notification_types.task_reminder',
  deadline_warning: 'ui_common.notification_types.deadline_warning',
  overdue: 'ui_common.notification_types.overdue',
  auto_schedule_done: 'ui_common.notification_types.auto_schedule_done',
  system: 'ui_common.notification_types.system',
  bot_update: 'ui_common.notification_types.bot_update',
};

// A notification was settled on some screen or by what it is about (read, the chat or task
// opened, the approval decided, the task done; backend utils/notify.py): every screen hears it.
export const RESOLVED_EVENT = 'bb:notifications-resolved';

/**
 * The buttons of a live notification's toast: the ones its phone and desktop notifications
 * carry (backend utils/push.py `action`, desktop/desktopNotifications.js). An approval is
 * approved or rejected, a task is done; a task and anything else can be opened.
 */
export function toastButtons(n, t, { open, decide, complete }) {
  const { act, ref } = n?.meta?.action || {};
  const openButton = { label: t('common.open'), onClick: open };
  if (act === 'approval' && ref) {
    return {
      action: { label: t('push.actions.approve'), onClick: () => decide(ref, true) },
      cancel: { label: t('push.actions.reject'), onClick: () => decide(ref, false) },
    };
  }
  if (act === 'task' && ref) {
    return { action: { label: t('push.actions.done'), onClick: () => complete(ref) }, cancel: openButton };
  }
  return { action: openButton };
}

/** The list after an event: a notification replaces the row with its id (one row per thing,
 * a newer one comes back to the top); a settled one is read, or gone when deleted. */
export function applyEvent(list, data) {
  if (data.type === 'notifications_resolved') {
    const ids = new Set(data.ids || []);
    const nids = new Set(data.nids || []);
    const hit = (n) => ids.has(n.id) || (n.nid && nids.has(n.nid));
    return data.deleted ? list.filter((n) => !hit(n)) : list.map((n) => (hit(n) && !n.is_read ? { ...n, is_read: true } : n));
  }
  return [data, ...list.filter((n) => n.id !== data.id)];
}

export const NotificationsProvider = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const listRef = useRef(notifications);
  listRef.current = notifications;
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsApi.getAll({ limit: 50 });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Silently fail - user may not be authenticated yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      setLoading(false);
      return undefined;
    }
    fetchNotifications();
    return openLiveStream({
      onEvent: (data) => {
        // Real-time cache invalidation: the user's data changed from another
        // session, device, or via MCP. Refetch the affected TanStack Query
        // caches so open views update without a manual page reload. Not a
        // user-facing notification - no toast, no list entry.
        if (data.type === 'invalidate') {
          // A database grid applies its own edits optimistically; its own
          // echo would only refetch what it already shows.
          if (data.origin && data.origin === CLIENT_ID
            && (data.keys || []).every((key) => key[0] === 'databases')) return;
          (data.keys || []).forEach((key) =>
            queryClient.invalidateQueries({ queryKey: key, cancelRefetch: false })
          );
          return;
        }

        if (data.type === 'notifications_resolved') {
          setNotifications((prev) => applyEvent(prev, data));
          (data.nids || []).forEach((nid) => toast.dismiss(nid));
          window.dispatchEvent(new CustomEvent(RESOLVED_EVENT, { detail: data }));
          // The count covers rows beyond the loaded page too: ask for it.
          fetchNotifications();
          queryClient.invalidateQueries({ queryKey: ['notifications'], cancelRefetch: false });
          return;
        }

        const wasUnread = listRef.current.some((n) => n.id === data.id && !n.is_read);
        setNotifications((prev) => applyEvent(prev, data));
        queryClient.invalidateQueries({ queryKey: ['notifications'], cancelRefetch: false });
        // About a chat or task the person has in front of them on some screen, it
        // arrives read (backend utils/presence.py): no count, no toast.
        if (data.is_read) return;
        if (!wasUnread) setUnreadCount((prev) => prev + 1);

        // Show a sonner toast for the incoming notification, unless it is about
        // the bot chat the user is looking at (they see it there already).
        if (!isAboutOpenChat(data)) showToast.current(data);
      },
      // What changed while the stream was down never arrives: refresh what is on screen.
      onReconnect: () => {
        queryClient.invalidateQueries({ cancelRefetch: false });
        fetchNotifications();
      },
    });
  }, [fetchNotifications]);

  const markRead = useCallback(async (id) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  }, []);

  // A live notification as a toast. Its text opens what it is about, as its row in the bell
  // does (read, then the exact task or chat); its buttons are those of its push.
  const showToast = useRef(null);
  showToast.current = (data) => {
    const t = (key) => i18n.t(key);
    const id = data.nid || data.id;  // a newer one about the same thing replaces it; settled, it goes
    const open = () => {
      toast.dismiss(id);
      if (!data.is_read) markRead(data.id);
      const url = notificationUrl(data);
      if (url) navigate(url);
    };
    const failed = (err) => toast.error(t(err?.response?.status === 409 ? 'push.actions.already' : 'errors.generic'));
    const decide = (approvalId, approve) => botsApi.decideApproval(approvalId, approve)
      .then(() => toast.success(t(approve ? 'bots.cards.approved' : 'bots.cards.declined')), failed);
    const complete = (taskId) => tasksApi.update(taskId, { status: 'completed' })
      .then(() => toast.success(t('push.actions.done')), failed);
    const link = (text, focusable) => (
      <button type="button" className="text-left cursor-pointer" tabIndex={focusable ? 0 : -1} onClick={open}>{text}</button>
    );
    const bot = botOf(data);
    // The title comes in the person's language (backend utils/notification_texts.py).
    toast(link(data.title || t(TYPE_LABEL_KEYS[data.type] || 'ui_common.notification_types.default'), true), {
      id,
      description: data.message ? link(data.message, false) : undefined,
      ...(bot ? { icon: <BotAvatar bot={bot} size={18} /> } : {}),
      ...toastButtons(data, t, { open, decide, complete }),
    });
  };

  const markAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }, []);

  const deleteNotification = useCallback(async (id) => {
    const notif = notifications.find((n) => n.id === id);
    try {
      await notificationsApi.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notif && !notif.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // ignore
    }
  }, [notifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        markRead,
        markAllRead,
        deleteNotification,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
